import api from 'services/api';
import { array, enforce, num, nullable, object, runSchema, str } from 'validation';

/**
 * A single queue as returned by nap-backend `GET /queues/:queue_id`
 * (routes/queues.js → getQueues, joined to zones / scheduling_windows). This is
 * the edit-relevant projection re-derived from the backend source, NOT the old
 * `QueueRow` interface, which carried fields the endpoint never returns
 * (`worker_name`, `connection_name`, `created_at`). The endpoint also returns
 * `queue_limit_allocation`, `updated_at`, `zone_id`, `scheduling_window_name`
 * and `scheduling_window_timezone`, which the edit form does not consume, so
 * they are dropped by the object parser. Deeply readonly — validated wire data.
 */
export interface FetchedQueue {
  readonly queue_id: number;
  readonly queue_name: string;
  readonly queue_description: string | null;
  readonly queue_status: string;
  readonly zone_name: string | null;
  readonly queue_limit: number;
  // Arrays are mutable-outer: the edit form assigns them straight into its
  // (mutable) form state, and `readonly T[]` is not assignable to `T[]`.
  readonly workers: { readonly worker_id: number; readonly worker_name: string; readonly priority: number }[];
  readonly connections: { readonly connection_id: number; readonly connection_name: string; readonly priority: number }[];
  readonly scheduling_window_id: number | null;
  readonly queue_allocation_statuses: string[];
  readonly folder_id: number | null;
}

const queueSchema = object<FetchedQueue>({
  queue_id: num(),
  queue_name: str(),
  queue_description: nullable(str()),
  queue_status: str(),
  zone_name: nullable(str()),
  queue_limit: num(),
  workers: array(object<{ worker_id: number; worker_name: string; priority: number }>({ worker_id: num(), worker_name: str(), priority: num() })),
  connections: array(
    object<{ connection_id: number; connection_name: string; priority: number }>({ connection_id: num(), connection_name: str(), priority: num() })
  ),
  scheduling_window_id: nullable(num()),
  queue_allocation_statuses: array(str()),
  folder_id: nullable(num()),
});

/**
 * Fetch a single queue by id for the edit form when the list slice is empty
 * (e.g. launched from the Workspace). Boundary-validated (issue #757 step 3);
 * structural failure or a drifted identity (`queue_id`) yields null so the
 * caller surfaces a "not found" instead of opening the dialog on a degraded row.
 */
export const getQueue = async (id: number): Promise<FetchedQueue | null> => {
  try {
    const res = await api.get(`/queues/${id}`);
    const result = runSchema(queueSchema, res.data, 'getQueue');
    if (!result.ok) return null;
    return enforce(result.value, result.warnings, ['queue_id']);
  } catch {
    return null;
  }
};
